import { GuideType, Method, TIdPInputField } from 'consts';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import {
	useField,
	useSSOConfigCurrentFieldsInitialized,
	useSSOConfigFields
} from 'contexts/SSOConfigFieldsContext';
import React, { useState } from 'react';
import { useFieldsConfigs } from '../../utils/fieldsConfigs';
import MDGapWrapper from '../MDRenderer/utilComponents/MDGapWrapper';

interface IIdPInputFieldProps {
	fieldName: TIdPInputField;
	displayName: string;
}

const IdPInputField: React.FC<IIdPInputFieldProps> = ({
	fieldName,
	displayName
}) => {
	const fieldsInitialized = useSSOConfigCurrentFieldsInitialized();
	// Guide-type-aware method WITHOUT pulling config data into this per-field leaf (a
	// useConfigs subscription here fires an async fetch on every field render, flooding
	// tests with act() warnings). Keep the raw context method for SSO/SCIM (unchanged), but
	// force Method.Xaa in the XAA guide: the raw method is a stale `saml` there (set when
	// SAML was configured earlier), which made the xaa-* field guard below throw and crash
	// the page into the router error boundary.
	const { method: rawMethod, isSubTenant, guideType } = useIdPConfigContext();
	const method = guideType === GuideType.XAA ? Method.Xaa : rawMethod;
	const [showError, setShowError] = useState(false);
	const { error, required, value, setValue } = useField(fieldName);
	const { getFieldValue } = useSSOConfigFields();
	const fieldsConfigs = useFieldsConfigs();
	const config = fieldsConfigs[fieldName];

	/* istanbul ignore if: no need to test this */
	if (!config) {
		throw new Error(`IdPInputField Error: unsupported field ${fieldName}`);
	}

	const { Component, props = {}, methods, disabledWhen } = config;

	/* istanbul ignore if: no need to test this */
	if (method && !methods.includes(method)) {
		throw new Error(
			`IdPInputField Error: invalid field "${fieldName}" for method ${method}`
		);
	}

	return (
		<MDGapWrapper
			onBlur={() => setShowError(true)}
			sx={{
				display: 'block'
			}}
		>
			<Component
				disabled={
					!fieldsInitialized ||
					isSubTenant ||
					(!!disabledWhen &&
						getFieldValue(disabledWhen.field) === disabledWhen.value)
				}
				{...props}
				key={fieldName}
				name={fieldName}
				required={required}
				displayName={displayName}
				value={value}
				onChange={setValue}
				error={showError && error?.message}
			/>
		</MDGapWrapper>
	);
};

export default IdPInputField;
